import React, { useState, useEffect } from "react";
import { Modal, Upload, Button, Select, Table, message, Steps, Space, Tag, Dropdown, Input, InputNumber } from "antd";
import { UploadOutlined, FileTextOutlined, CheckCircleOutlined, EditOutlined, SwapOutlined, PlusOutlined, CloseOutlined } from "@ant-design/icons";
import type { UploadFile } from "antd/es/upload/interface";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { baseService } from "~/services/base";
import { useGetClients } from "~/services/client/client.queries";
import { InventoryType, InventoryStatusCode, PackingTypes } from "~/utils/constants";

type ParsedInventory = {
  title?: string;
  artistName?: string;
  medium?: string;
  description?: string;
  pieceCount?: string;
  packageCount?: string;
  length?: number;
  width?: number;
  height?: number;
  weight?: number;
  value?: number;
  clientInternalRef?: string;
  notes?: string;
};

type ColumnField = "title" | "artistName" | "medium" | "clientInternalRef" | "description" | "notes";
type DimensionField = "length" | "width" | "height";

type PdfImportModalProps = {
  open: boolean;
  onClose: () => void;
  initialFile?: File;
  initialClientId?: string;
  onSuccess?: () => void;
};

export const PdfImportModal: React.FC<PdfImportModalProps> = ({ open, onClose, initialFile, initialClientId, onSuccess }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedClientId, setSelectedClientId] = useState<string | undefined>(initialClientId);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [parsedData, setParsedData] = useState<ParsedInventory[]>([]);
  const [editedData, setEditedData] = useState<ParsedInventory[]>([]);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [parsingFailed, setParsingFailed] = useState(false);

  const queryClient = useQueryClient();
  const { data: clientsResponse, isLoading: clientsLoading } = useGetClients({
    params: { status: "ACTIVE", sortByName: "ASC", getAll: true },
  });
  const clients = clientsResponse?.items || [];

  const parseMutation = useMutation({
    mutationFn: async (data: { file: File; clientId: string }) => {
      const formData = new FormData();
      formData.append("file", data.file);
      formData.append("clientId", data.clientId);

      const response = await baseService.post("/inventories/parse-pdf", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return response.data;
    },
    onSuccess: (data) => {
      setParsedData(data.inventories);
      setEditedData(data.inventories);
      setCurrentStep(2);
      setParsingFailed(false);
      message.success(`Successfully parsed ${data.count} inventory items`);
    },
    onError: (error: any) => {
      console.error("PDF parsing error:", error);
      const errorMessage = error.response?.data?.message || error.message || "Failed to parse PDF";
      message.error(errorMessage);
      // Set parsing failed flag and allow manual entry
      setParsingFailed(true);
      setCurrentStep(2); // Go to review step even if parsing failed
      // Create empty row for manual entry
      setEditedData([{
        title: "",
        artistName: "",
        medium: "",
        description: "",
        clientInternalRef: "",
        length: undefined,
        width: undefined,
        height: undefined,
        weight: undefined,
        value: undefined,
        notes: "",
      }]);
    },
  });

  const importMutation = useMutation({
    mutationFn: async (data: { clientId: string; inventories: ParsedInventory[] }) => {
      const response = await baseService.post("/inventories/import-from-pdf", data);
      return response.data;
    },
    onSuccess: (data) => {
      message.success(`Successfully imported ${data.created} inventory items`);
      queryClient.invalidateQueries({ queryKey: ["inventories"] });
      onSuccess?.();
      handleClose();
    },
    onError: (error: any) => {
      message.error(error.response?.data?.message || "Failed to import inventory");
    },
  });

  const handleUpload = () => {
    if (!selectedClientId) {
      message.error("Please select a client first");
      return;
    }

    if (fileList.length === 0) {
      message.error("Please select a PDF file");
      return;
    }

    const file = fileList[0].originFileObj as File;
    
    // Create object URL for PDF preview
    const url = URL.createObjectURL(file);
    setPdfUrl(url);
    
    // Try to parse, but if it fails, we'll allow manual entry
    parseMutation.mutate({ file, clientId: selectedClientId });
  };

  const handleAddRow = () => {
    const newRow: ParsedInventory = {
      title: "",
      artistName: "",
      medium: "",
      description: "",
      clientInternalRef: "",
      length: undefined,
      width: undefined,
      height: undefined,
      weight: undefined,
      value: undefined,
      notes: "",
    };
    setEditedData([...editedData, newRow]);
  };

  const handleRemoveRow = (index: number) => {
    const updatedData = editedData.filter((_, i) => i !== index);
    setEditedData(updatedData);
  };

  const handleImport = () => {
    if (!selectedClientId) {
      message.error("Please select a client");
      return;
    }

    importMutation.mutate({
      clientId: selectedClientId,
      inventories: editedData,
    });
  };

  const handleClose = () => {
    setCurrentStep(0);
    setSelectedClientId(initialClientId);
    setFileList([]);
    setParsedData([]);
    setEditedData([]);
    setParsingFailed(false);
    // Clean up PDF URL
    if (pdfUrl) {
      URL.revokeObjectURL(pdfUrl);
      setPdfUrl(null);
    }
    onClose();
  };

  // Handle initial file when modal opens
  useEffect(() => {
    if (open && initialFile) {
      // Set the file in the file list
      const uploadFile: UploadFile = {
        uid: '-1',
        name: initialFile.name,
        status: 'done',
        originFileObj: initialFile,
      };
      setFileList([uploadFile]);
      
      // Create object URL for PDF preview
      const url = URL.createObjectURL(initialFile);
      setPdfUrl(url);
      
      // If client ID is provided, go to step 1 (upload step) so user can parse
      if (initialClientId) {
        setSelectedClientId(initialClientId);
        setCurrentStep(1);
      } else {
        // No client ID, start at step 0
        setCurrentStep(0);
      }
    } else if (open && !initialFile) {
      // Reset when opening without initial file
      setCurrentStep(0);
      setFileList([]);
      setSelectedClientId(initialClientId);
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
        setPdfUrl(null);
      }
    }
    
    // Cleanup function
    return () => {
      if (!open && pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [open, initialFile, initialClientId]);

  const handleFieldChange = (index: number, field: string, value: any) => {
    const updatedData = [...editedData];
    updatedData[index] = { ...updatedData[index], [field]: value };
    setEditedData(updatedData);
  };

  const handleSwapColumns = (fromField: ColumnField, toField: ColumnField) => {
    const updatedData = editedData.map((item) => {
      const temp = item[fromField];
      return {
        ...item,
        [fromField]: item[toField],
        [toField]: temp,
      };
    });
    setEditedData(updatedData);
    message.success(`Swapped ${fromField} with ${toField}`);
  };

  const getSwapMenuItems = (currentField: ColumnField) => {
    const fields: { key: ColumnField; label: string }[] = [
      { key: "title", label: "Title" },
      { key: "artistName", label: "Artist" },
      { key: "medium", label: "Medium" },
      { key: "clientInternalRef", label: "Client Ref" },
      { key: "description", label: "Description" },
      { key: "notes", label: "Notes" },
    ];

    return fields
      .filter((f) => f.key !== currentField)
      .map((field) => ({
        key: field.key,
        label: field.label,
        onClick: () => handleSwapColumns(currentField, field.key),
      }));
  };

  const handleSwapDimensions = (fromField: DimensionField, toField: DimensionField) => {
    const updatedData = editedData.map((item) => {
      const temp = item[fromField];
      return {
        ...item,
        [fromField]: item[toField],
        [toField]: temp,
      };
    });
    setEditedData(updatedData);
    message.success(`Swapped ${fromField} with ${toField}`);
  };

  const getDimensionSwapMenuItems = (currentField: DimensionField) => {
    const fields: { key: DimensionField; label: string }[] = [
      { key: "length", label: "Length" },
      { key: "width", label: "Width" },
      { key: "height", label: "Height" },
    ];

    return fields
      .filter((f) => f.key !== currentField)
      .map((field) => ({
        key: field.key,
        label: field.label,
        onClick: () => handleSwapDimensions(currentField, field.key),
      }));
  };

  const columns = [
    {
      title: (
        <div className="flex items-center justify-between">
          <span>Title</span>
          <Dropdown menu={{ items: getSwapMenuItems("title") }} trigger={["click"]}>
            <Button type="text" size="small" icon={<SwapOutlined />} />
          </Dropdown>
        </div>
      ),
      dataIndex: "title",
      key: "title",
      width: 200,
      ellipsis: true,
      render: (text: string, record: ParsedInventory, index: number) => (
        <input
          type="text"
          value={text || ""}
          onChange={(e) => handleFieldChange(index, "title", e.target.value)}
          className="w-full border-0 focus:outline-none focus:ring-1 focus:ring-blue-500 rounded px-2 py-1"
        />
      ),
    },
    {
      title: (
        <div className="flex items-center justify-between">
          <span>Artist</span>
          <Dropdown menu={{ items: getSwapMenuItems("artistName") }} trigger={["click"]}>
            <Button type="text" size="small" icon={<SwapOutlined />} />
          </Dropdown>
        </div>
      ),
      dataIndex: "artistName",
      key: "artistName",
      width: 150,
      render: (text: string, record: ParsedInventory, index: number) => (
        <input
          type="text"
          value={text || ""}
          onChange={(e) => handleFieldChange(index, "artistName", e.target.value)}
          className="w-full border-0 focus:outline-none focus:ring-1 focus:ring-blue-500 rounded px-2 py-1"
        />
      ),
    },
    {
      title: (
        <div className="flex items-center justify-between">
          <span>Medium</span>
          <Dropdown menu={{ items: getSwapMenuItems("medium") }} trigger={["click"]}>
            <Button type="text" size="small" icon={<SwapOutlined />} />
          </Dropdown>
        </div>
      ),
      dataIndex: "medium",
      key: "medium",
      width: 150,
      render: (text: string, record: ParsedInventory, index: number) => (
        <input
          type="text"
          value={text || ""}
          onChange={(e) => handleFieldChange(index, "medium", e.target.value)}
          className="w-full border-0 focus:outline-none focus:ring-1 focus:ring-blue-500 rounded px-2 py-1"
        />
      ),
    },
    {
      title: (
        <div className="flex items-center justify-between">
          <span>Client Ref</span>
          <Dropdown menu={{ items: getSwapMenuItems("clientInternalRef") }} trigger={["click"]}>
            <Button type="text" size="small" icon={<SwapOutlined />} />
          </Dropdown>
        </div>
      ),
      dataIndex: "clientInternalRef",
      key: "clientInternalRef",
      width: 150,
      render: (text: string, record: ParsedInventory, index: number) => (
        <input
          type="text"
          value={text || ""}
          onChange={(e) => handleFieldChange(index, "clientInternalRef", e.target.value)}
          className="w-full border-0 focus:outline-none focus:ring-1 focus:ring-blue-500 rounded px-2 py-1"
        />
      ),
    },
    {
      title: (
        <div className="flex items-center justify-between">
          <span>Length</span>
          <Dropdown menu={{ items: getDimensionSwapMenuItems("length") }} trigger={["click"]}>
            <Button type="text" size="small" icon={<SwapOutlined />} />
          </Dropdown>
        </div>
      ),
      dataIndex: "length",
      key: "length",
      width: 100,
      render: (value: number, record: ParsedInventory, index: number) => (
        <input
          type="number"
          value={value || ""}
          onChange={(e) => handleFieldChange(index, "length", parseFloat(e.target.value) || undefined)}
          placeholder="inches"
          className="w-full border-0 focus:outline-none focus:ring-1 focus:ring-blue-500 rounded px-2 py-1"
        />
      ),
    },
    {
      title: (
        <div className="flex items-center justify-between">
          <span>Width</span>
          <Dropdown menu={{ items: getDimensionSwapMenuItems("width") }} trigger={["click"]}>
            <Button type="text" size="small" icon={<SwapOutlined />} />
          </Dropdown>
        </div>
      ),
      dataIndex: "width",
      key: "width",
      width: 100,
      render: (value: number, record: ParsedInventory, index: number) => (
        <input
          type="number"
          value={value || ""}
          onChange={(e) => handleFieldChange(index, "width", parseFloat(e.target.value) || undefined)}
          placeholder="inches"
          className="w-full border-0 focus:outline-none focus:ring-1 focus:ring-blue-500 rounded px-2 py-1"
        />
      ),
    },
    {
      title: (
        <div className="flex items-center justify-between">
          <span>Height</span>
          <Dropdown menu={{ items: getDimensionSwapMenuItems("height") }} trigger={["click"]}>
            <Button type="text" size="small" icon={<SwapOutlined />} />
          </Dropdown>
        </div>
      ),
      dataIndex: "height",
      key: "height",
      width: 100,
      render: (value: number, record: ParsedInventory, index: number) => (
        <input
          type="number"
          value={value || ""}
          onChange={(e) => handleFieldChange(index, "height", parseFloat(e.target.value) || undefined)}
          placeholder="inches"
          className="w-full border-0 focus:outline-none focus:ring-1 focus:ring-blue-500 rounded px-2 py-1"
        />
      ),
    },
    {
      title: "Pieces",
      dataIndex: "pieceCount",
      key: "pieceCount",
      width: 80,
      render: (text: string, record: ParsedInventory, index: number) => (
        <input
          type="text"
          value={text}
          onChange={(e) => handleFieldChange(index, "pieceCount", e.target.value)}
          className="w-full border-0 focus:outline-none focus:ring-1 focus:ring-blue-500 rounded px-2 py-1"
        />
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 80,
      fixed: "right" as const,
      render: (_: any, record: ParsedInventory, index: number) => (
        <Button
          type="link"
          danger
          icon={<CloseOutlined />}
          onClick={() => handleRemoveRow(index)}
          size="small"
        />
      ),
    },
  ];

  const steps = [
    {
      title: "Select Client",
      icon: <EditOutlined />,
    },
    {
      title: "Upload PDF",
      icon: <FileTextOutlined />,
    },
    {
      title: "Review & Import",
      icon: <CheckCircleOutlined />,
    },
  ];

  return (
    <Modal
      title="Import Inventory from PDF"
      open={open}
      onCancel={handleClose}
      width={currentStep === 2 ? 1200 : 600}
      footer={null}
    >
      <Steps current={currentStep} items={steps} className="mb-6" />

      {currentStep === 0 && (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">
              Select Client <span className="text-red-500">*</span>
            </label>
            <Select
              className="w-full"
              placeholder="Search clients..."
              value={selectedClientId}
              onChange={setSelectedClientId}
              loading={clientsLoading}
              showSearch
              optionFilterProp="label"
              filterOption={(input, option) =>
                (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
              }
              size="large"
              options={clients.map((client) => ({
                value: client.id,
                label:
                  client.companyName ||
                  [client.firstName, client.lastName]
                    .filter(Boolean)
                    .join(" ")
                    .trim() ||
                  "Unnamed Client",
              }))}
            />
          </div>

          <div className="flex justify-end mt-6">
            <Space>
              <Button onClick={handleClose}>Cancel</Button>
              <Button
                type="primary"
                onClick={() => setCurrentStep(1)}
                disabled={!selectedClientId}
              >
                Next
              </Button>
            </Space>
          </div>
        </div>
      )}

      {currentStep === 1 && (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">
              Upload PDF File <span className="text-red-500">*</span>
            </label>
            <Upload
              fileList={fileList}
              onChange={({ fileList }) => setFileList(fileList)}
              beforeUpload={() => false}
              accept=".pdf"
              maxCount={1}
            >
              <Button icon={<UploadOutlined />}>Select PDF</Button>
            </Upload>
            <p className="text-sm text-gray-500 mt-2">
              Upload a PDF containing inventory information. The AI will automatically
              extract and structure the data for you.
            </p>
          </div>

          <div className="flex justify-end mt-6">
            <Space>
              <Button onClick={() => setCurrentStep(0)}>Back</Button>
              <Button
                type="primary"
                onClick={handleUpload}
                loading={parseMutation.isPending}
                disabled={fileList.length === 0}
              >
                Parse PDF
              </Button>
            </Space>
          </div>
        </div>
      )}

      {currentStep === 2 && (
        <div className="space-y-4">
          {parsingFailed ? (
            <div className="bg-yellow-50 border border-yellow-200 rounded p-3 mb-4">
              <p className="text-sm text-yellow-800">
                <strong>PDF parsing failed.</strong> Please manually enter the inventory details below. 
                The PDF is displayed on the right for reference.
              </p>
            </div>
          ) : (
            <div className="bg-blue-50 border border-blue-200 rounded p-3 mb-4">
              <p className="text-sm text-blue-800">
                <strong>{editedData.length}</strong> inventory items were extracted.
                Review the data below and click Import to add them to your inventory.
              </p>
            </div>
          )}

          <div className="flex gap-4">
            {/* PDF Viewer */}
            {pdfUrl && (
              <div className="w-1/2 border rounded-lg overflow-hidden" style={{ maxHeight: "600px" }}>
                <iframe
                  src={pdfUrl}
                  className="w-full h-full"
                  style={{ minHeight: "500px" }}
                  title="PDF Preview"
                />
              </div>
            )}

            {/* Data Entry Table */}
              <div className={pdfUrl ? "w-1/2" : "w-full"}>
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium">Inventory Items</span>
                <Button
                  type="dashed"
                  icon={<PlusOutlined />}
                  onClick={handleAddRow}
                  size="small"
                >
                  Add Row
                </Button>
              </div>
              <div style={{ maxHeight: "600px", overflow: "auto" }}>
                <Table
                  dataSource={editedData}
                  columns={columns}
                  rowKey={(_, index) => `row-${index}`}
                  scroll={{ x: 800 }}
                  pagination={false}
                  size="small"
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end mt-6">
            <Space>
              <Button onClick={handleClose}>Cancel</Button>
              <Button
                type="primary"
                onClick={handleImport}
                loading={importMutation.isPending}
                disabled={editedData.length === 0}
              >
                Import {editedData.length} Item{editedData.length !== 1 ? "s" : ""}
              </Button>
            </Space>
          </div>
        </div>
      )}
    </Modal>
  );
};